import { apiBeta } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { HOME_BROWSE_PATH, SEARCH_PATH } from 'src/common/constants/paths';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { selectTunedGuideId } from 'src/common/selectors/player';
import { isCategory, isValidGuideId } from 'src/common/utils/guideItemTypes';

class ScreenViewTracker {
  #pathname?: string;
  #guideId?: string;
  #isCategory = false;

  init() {
    // In the future, the store will be migrated from Redux to Zustand, which provides state/prevState args,
    // so let's not add these as class properties.
    let _previousIsNowPlayingDialogOpen = false;
    let _previousTunedGuideId = '';

    storeSingleton.subscribe(() => {
      const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(
        storeSingleton.state,
      );
      const tunedGuideId = selectTunedGuideId(storeSingleton.state);

      try {
        if (
          (isNowPlayingDialogOpen && !_previousIsNowPlayingDialogOpen) ||
          (isNowPlayingDialogOpen && tunedGuideId !== _previousTunedGuideId)
        ) {
          apiBeta.telemetry.setScreen({
            name: 'NowPlaying',
            guideId: tunedGuideId,
          });
          return;
        }

        if (!isNowPlayingDialogOpen && _previousIsNowPlayingDialogOpen) {
          this.updateScreen();
          return;
        }
      } finally {
        _previousIsNowPlayingDialogOpen = isNowPlayingDialogOpen;
        _previousTunedGuideId = tunedGuideId;
      }
    });
  }

  onRouteChange(routeProps: {
    guideContext: { guideId: string };
    matchUrl: string;
  }) {
    this.#guideId = routeProps.guideContext.guideId;
    this.#isCategory =
      !isValidGuideId(this.#guideId as string) || isCategory(this.#guideId);
    this.updateScreen(routeProps.matchUrl as string);
  }

  updateScreen(pathname?: string) {
    this.#pathname = pathname || this.#pathname;
    const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(
      storeSingleton.state,
    );

    if (!this.#pathname || isNowPlayingDialogOpen) {
      return;
    }

    if (this.#pathname === HOME_BROWSE_PATH) {
      apiBeta.telemetry.setScreen({ name: 'Home', guideId: this.#guideId });
      return;
    }

    if (this.#pathname === SEARCH_PATH) {
      apiBeta.telemetry.setScreen({ name: 'Search' });
      return;
    }

    if (this.#pathname.startsWith('/user/')) {
      apiBeta.telemetry.setScreen({ name: 'Library' });
      return;
    }

    // The existence of a guide id implies a category/profile route.
    if (this.#guideId) {
      apiBeta.telemetry.setScreen({
        name: this.#isCategory ? 'Browse' : 'Profile',
        guideId: this.#guideId,
      });
      return;
    }

    apiBeta.telemetry.clearScreen();
  }
}

export const screenViewTracker = new ScreenViewTracker();
